import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown, faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { faCheck, faSpinner } from "@fortawesome/pro-solid-svg-icons";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { Icon } from "@/components";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { selectJobByProspectId } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import { useGetSingleListPersonQuery } from "@/pages/Private/redux/list/list.api";
import {
	setFeedPanelContextView,
	setFeedSelectedCardId,
	setFeedSelectedPerson,
} from "@/pages/Private/redux/feed/feed.slice";

import { ReachOutMenu } from "@/pages/Private/pages/List/components/ReachOut/ReachOutMenu";

import { feedCardKey } from "../../utils/feedCardKey";
import { useFeedReviewNow } from "../../utils/useFeedReviewNow";

const CHIP_BASE = "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold";

interface ReachOutButtonProps {
	card: FeedRunCard;
	/** The card's currently selected person; the button follows the switcher (D3). */
	person: FeedPerson | null;
}

/**
 * The run-card primary action (PREC-3635 P6 D3): idle → the shared list-page
 * {@link ReachOutMenu} (campaign picker, GenerateBar "Let AI write", manual),
 * then generating / queued (the backend's live status, D2) → ready →
 * review-now into the docked panel's Chat (D6). Renders nothing when the
 * person lacks a prospect or listPerson (degraded card, P5 D7).
 */
export const ReachOutButton: FunctionComponent<ReachOutButtonProps> = ({ card, person }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const job = useAppSelector((state) => selectJobByProspectId(state, person?.prospectId));
	const { reviewJobNow } = useFeedReviewNow();

	const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);

	// The shared menu operates on the full ListPerson (list + campaignConfig,
	// prospect, copywriting state) — fetched lazily on first open.
	const { data: listPerson } = useGetSingleListPersonQuery(person?.listPersonId ?? 0, {
		skip: !menuAnchorRect || !person?.listPersonId,
	});

	// Without a real prospect the backend cannot resolve the listPerson — the slot
	// stays empty on degraded / placeholder cards (D3, P5 D7).
	if (!person?.prospectId || !person.listPersonId || person.needsProspectSelection) {
		return null;
	}

	const firstName = (person.name ?? "").split(" ")[0] ?? "";

	const cardKey = feedCardKey(card);

	// The card's main signal — the strongest signal of the run (rendered first, driving the
	// "Prio 1 Signal" headline). "Let AI write a sequence" defaults to it (PREC-3710).
	const mainSignal = card.signals.find((signal) => signal.belongsToRun) ?? card.signals[0] ?? null;

	/** Select the card for the docked panel and open it on Chat (D6). */
	const openChat = () => {
		dispatch(setFeedSelectedCardId(cardKey));
		dispatch(setFeedSelectedPerson({ runId: cardKey, listPersonId: person.listPersonId }));
		dispatch(setFeedPanelContextView("chat"));
	};

	if (job?.status === "RUNNING") {
		return (
			<span
				className={`${CHIP_BASE} border-brand-light bg-light-blue text-brand-light`}
				data-testid="feed-reach-out-generating"
			>
				<Icon className="h-3 w-3 animate-spin" icon={faSpinner} />
				{t("feed.reachOut.generating", { name: firstName })}
			</span>
		);
	}

	if (job?.status === "QUEUED") {
		return (
			<span
				className={`${CHIP_BASE} border-border-light bg-background-light text-text-tertiary`}
				data-testid="feed-reach-out-queued"
			>
				<span className="h-2 w-2 animate-pulse rounded-full bg-text-tertiary" />
				{t("feed.reachOut.queued", { name: firstName })}
			</span>
		);
	}

	if (job?.status === "COMPLETED" && !job.reviewedAt) {
		return (
			<button
				className={`${CHIP_BASE} cursor-pointer border-transparent bg-background-success text-text-success hover:brightness-95`}
				data-testid="feed-reach-out-ready"
				type="button"
				onClick={() => reviewJobNow(job, { runId: cardKey, listPersonId: person.listPersonId })}
			>
				<Icon className="h-3 w-3" icon={faCheck} />
				{t("feed.reachOut.ready", { name: firstName })}
			</button>
		);
	}

	// Drafts already generated earlier (persisted on the listPerson) — the live job
	// states above win while a regeneration is running; otherwise the idle reach-out
	// is replaced by a review affordance that opens the docked panel's Chat.
	if (person.copywritingCompleted) {
		return (
			<button
				className={`${CHIP_BASE} cursor-pointer border-transparent bg-background-success text-text-success hover:brightness-95`}
				data-testid="feed-reach-out-done"
				type="button"
				onClick={openChat}
			>
				<Icon className="h-3 w-3" icon={faCheck} />
				{t("feed.reachOut.done", { name: firstName })}
			</button>
		);
	}

	return (
		<>
			<button
				className={`${CHIP_BASE} cursor-pointer border-transparent bg-blue-action text-white hover:bg-blue-action-dark`}
				data-testid="feed-reach-out-launch"
				type="button"
				onClick={(event) => setMenuAnchorRect(event.currentTarget.getBoundingClientRect())}
			>
				<Icon
					className={menuAnchorRect && !listPerson ? "h-3 w-3 animate-spin" : "h-3 w-3"}
					icon={menuAnchorRect && !listPerson ? faSpinner : faPaperPlane}
				/>
				{t("feed.reachOut.button", { name: firstName })}
				<Icon className="h-2.5 w-2.5 opacity-80" icon={faChevronDown} />
			</button>

			{menuAnchorRect && listPerson && (
				<ReachOutMenu
					// "Let AI write a sequence" defaults to the card's main signal (PREC-3710): it shows
					// as a removable chip and is used as the outreach trigger. Removing it writes without
					// a signal. Falls back to the run summary text for the chip label when unavailable.
					aiSeedSignalId={mainSignal?.signalId}
					aiTriggerDescription={
						mainSignal?.line ?? mainSignal?.title ?? card.summaryWhat ?? card.title ?? null
					}
					anchorRect={menuAnchorRect}
					// The feed reaches out per signal — the list-campaign paths don't apply here.
					hideCampaignActions
					target={{ kind: "lead", row: listPerson }}
					onClose={() => setMenuAnchorRect(null)}
					onManual={openChat}
				/>
			)}
		</>
	);
};
